import { FLEET_MISSION_DEFINITIONS, type TeamPlanetFlight } from '@kaeliux/shared';
import { useTeamPlanetFlights } from '../../hooks/useTeamPlanet';
import { formatClock, formatCoords, shipSummary } from '../../utils/fleetFormat';
import { formatFullNumber } from '../../utils/formatters';
import { MISSION_TONE } from '../../utils/missionMeta';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';
import { Chip } from '../ui/Chip';
import { Panel } from '../ui/Panel';

function nextCheckpoint(flight: TeamPlanetFlight): { label: string; at: Date } {
    return flight.status === 'outbound'
        ? { label: 'Arrives', at: flight.arrivesAt }
        : { label: 'Returns', at: flight.returnsAt };
}

/**
 * Read-only flight coordinator (issue #294 follow-up): every member's
 * flights heading to the Team planet and every fleet away from it, so the
 * whole alliance can see what is about to dock — not only the leader, and
 * not only their own flights. Also the Team planet's own flight slots: every
 * flight away from it takes one, whoever sent it.
 */
export default function TeamPlanetFlightsPanel() {
    // No interval of its own: the HUD already polls this query on every
    // Team planet route, and a second timer would double the reads.
    const flightsQuery = useTeamPlanetFlights();
    const view = flightsQuery.data;

    return (
        <Panel variant="solid" padding="md" className="flex flex-col gap-3" data-testid="team-planet-flights">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="text-title text-content-primary">Team Planet Flights</h2>
                <div className="flex items-center gap-2 flex-wrap">
                    {view && view.incomingStationPopulation > 0 && (
                        <Chip tone="info" data-testid="team-planet-incoming-population">
                            Incoming Station population: +{formatFullNumber(view.incomingStationPopulation)}
                        </Chip>
                    )}
                    {view && view.maxActive > 0 && (
                        <Chip tone={view.activeCount >= view.maxActive ? 'warning' : 'neutral'} data-testid="team-planet-flight-slots">
                            Flights: {view.activeCount} / {view.maxActive}
                        </Chip>
                    )}
                </div>
            </div>
            {flightsQuery.isError && (
                <p className="text-status-danger text-label">Failed to load the Team planet&apos;s flights.</p>
            )}
            {view && view.flights.length === 0 && (
                <p className="text-content-muted text-label">No flights to or from the Team planet.</p>
            )}
            {view?.flights.map((flight) => {
                const checkpoint = nextCheckpoint(flight);
                return (
                    <Card key={flight.id} className="flex flex-col gap-1" data-testid="team-planet-flight-row" data-direction={flight.direction}>
                        <div className="flex items-center gap-2 flex-wrap">
                            <Badge tone={MISSION_TONE[flight.mission]}>{FLEET_MISSION_DEFINITIONS[flight.mission].name}</Badge>
                            <Chip tone={flight.direction === 'inbound' ? 'info' : 'neutral'}>
                                {flight.direction === 'inbound' ? 'Inbound' : 'Outbound'}
                            </Chip>
                            <span className="text-content-secondary text-label">{flight.ownerName}</span>
                            <span className="text-content-muted text-label">
                                {formatCoords(flight.origin)} → {formatCoords(flight.target)}
                            </span>
                        </div>
                        <p className="text-content-secondary text-body truncate" title={shipSummary(flight.ships, flight.race)}>
                            {shipSummary(flight.ships, flight.race)}
                        </p>
                        <p className="text-content-muted text-label">
                            Population {formatFullNumber(flight.population)} · {checkpoint.label} {formatClock(checkpoint.at)}
                        </p>
                    </Card>
                );
            })}
        </Panel>
    );
}
